import { useWindowSize } from "@/utils/hooksUtils";
import { Button, Card, ConfigProvider, Empty, Flex, Select, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useEnumeration,
  useGetDashboardAgentEducations,
  useGetPartnerConfigsAndSsoProperties,
} from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { AgentEducationType } from "../../../../agent/enums";
import { isAgentTipper } from "../../../../agent/utils";
import { useCurrentAgentAccount, useHasPermissions } from "../../../../auth/hooks";
import { EnumerationType } from "../../../../enumerations/enums";
import { PartnerSsoType } from "../../../../partner/enums";
import { DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH } from "../../../utils";
import { SquareGrid } from "./components/SquareGrid";

const ACADEMY_TYPE_MAP: Record<
  AgentEducationType.FINANCIAL_ACADEMY | AgentEducationType.PERSONAL_ACADEMY,
  AgentEducationType[]
> = {
  [AgentEducationType.FINANCIAL_ACADEMY]: [
    AgentEducationType.FINANCIAL_ACADEMY,
    AgentEducationType.FINANCIAL_ACADEMY_COMPLETION,
  ],
  [AgentEducationType.PERSONAL_ACADEMY]: [AgentEducationType.PERSONAL_ACADEMY],
};

export const DashboardAgentEducationsWidget = () => {
  const { t } = useTranslation();

  const windowSize = useWindowSize();

  const currentAgent = useCurrentAgentAccount()?.agent;
  const isTipper = currentAgent ? isAgentTipper(currentAgent) : false;

  const [educationType, setEducationType] = useState<
    AgentEducationType.FINANCIAL_ACADEMY | AgentEducationType.PERSONAL_ACADEMY
  >(isTipper ? AgentEducationType.FINANCIAL_ACADEMY : AgentEducationType.PERSONAL_ACADEMY);

  const educations = useEnumeration(EnumerationType.AGENT_EDUCATIONS).filter((education) =>
    ACADEMY_TYPE_MAP[educationType]?.includes(education.type),
  );

  const hasPartnerOthersPermission = useHasPermissions(Permission.PARTNER_OTHERS);

  const { data } = useGetDashboardAgentEducations({ educationType });
  const { data: partnerConfigs } = useGetPartnerConfigsAndSsoProperties(hasPartnerOthersPermission);

  const vzdyViacLink = partnerConfigs?.configs.find((config) => config.ssoType === PartnerSsoType.VZDY_VIAC)?.link;

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Flex flex="none" justify="space-between" style={{ marginBottom: 8 }}>
        <h2 style={{ fontWeight: 600, fontSize: 28, lineHeight: "36px" }}>{t("dashboard.agentEducations.title")}</h2>

        <Space
          size={windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH ? "large" : "small"}
          style={{ marginBottom: 14 }}
        >
          <ConfigProvider theme={{ token: { borderRadius: 18 } }}>
            <Select
              style={{ width: 260 }}
              value={educationType}
              options={[AgentEducationType.FINANCIAL_ACADEMY, AgentEducationType.PERSONAL_ACADEMY].map((type) => ({
                label: t(`agent.education.enums.agentEducationType.${type}`),
                value: type,
              }))}
              onChange={setEducationType}
            />
          </ConfigProvider>

          {vzdyViacLink ? (
            <Button href={vzdyViacLink} target="_blank" type="primary">
              VždyViac Portál
            </Button>
          ) : undefined}
        </Space>
      </Flex>

      <Flex flex={1}>
        {educations?.length ? (
          <SquareGrid
            items={educations.map((education) => ({
              id: education.id,
              name: education.name,
              completedDate: data?.find((completedEducation) => completedEducation.education?.id === education.id)
                ?.completedDate,
            }))}
          />
        ) : (
          <Card style={{ borderRadius: 18, width: "100%" }}>
            <Flex justify="center">
              <Empty className="margin-top-medium" />
            </Flex>
          </Card>
        )}
      </Flex>
    </Flex>
  );
};
